/**
 * Persist user scenes/scripts in localStorage (and optional download).
 */

import type { SceneResource, ScriptRef } from './types';
import { parseScene, serializeScene } from './SceneDocument';
import { getBuiltinScenes, getBuiltinSceneByPath } from './builtinScenes';

const SCENES_KEY = 'fe-editor-scenes-v1';
const ACTIVE_SCENE_KEY = 'fe-editor-active-scene';

export interface SceneIndexEntry {
  id: string;
  name: string;
  kind: SceneResource['kind'];
  path: string;
  updatedAt: string;
  builtin?: boolean;
}

function loadUserMap(): Record<string, SceneResource> {
  try {
    const raw = localStorage.getItem(SCENES_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, SceneResource>;
  } catch {
    return {};
  }
}

function saveUserMap(map: Record<string, SceneResource>): void {
  localStorage.setItem(SCENES_KEY, JSON.stringify(map));
}

export function listScenes(): SceneIndexEntry[] {
  const builtins = getBuiltinScenes().map((s) => ({
    id: s.id,
    name: s.name,
    kind: s.kind,
    path: s.path,
    updatedAt: s.updatedAt,
    builtin: true,
  }));
  const user = Object.values(loadUserMap()).map((s) => ({
    id: s.id,
    name: s.name,
    kind: s.kind,
    path: s.path,
    updatedAt: s.updatedAt,
    builtin: false,
  }));
  return [...builtins, ...user].sort((a, b) => a.path.localeCompare(b.path));
}

export function loadSceneByPath(path: string): SceneResource | null {
  const map = loadUserMap();
  const user = Object.values(map).find((s) => s.path === path);
  if (user) return structuredClone(user);
  const builtin = getBuiltinSceneByPath(path);
  return builtin ? structuredClone(builtin) : null;
}

export function loadSceneById(id: string): SceneResource | null {
  const map = loadUserMap();
  if (map[id]) return structuredClone(map[id]);
  const builtin = getBuiltinScenes().find((s) => s.id === id);
  return builtin ? structuredClone(builtin) : null;
}

export function saveScene(scene: SceneResource): SceneResource {
  const map = loadUserMap();
  // If saving a builtin, fork to a user copy with new id unless already user
  let toSave = scene;
  if (scene.builtin) {
    toSave = {
      ...structuredClone(scene),
      id: scene.id.startsWith('user_') ? scene.id : `user_${scene.id}_${Date.now().toString(36)}`,
      builtin: false,
      path: scene.path.includes('/user/') ? scene.path : scene.path.replace('res://', 'res://user/'),
      updatedAt: new Date().toISOString(),
    };
  } else {
    toSave = { ...scene, builtin: false, updatedAt: new Date().toISOString() };
  }
  map[toSave.id] = toSave;
  saveUserMap(map);
  return toSave;
}

export function deleteScene(id: string): boolean {
  const map = loadUserMap();
  if (!map[id] || map[id].builtin) return false;
  delete map[id];
  saveUserMap(map);
  return true;
}

export function getActiveScenePath(): string | null {
  return localStorage.getItem(ACTIVE_SCENE_KEY);
}

export function setActiveScenePath(path: string | null): void {
  if (path) localStorage.setItem(ACTIVE_SCENE_KEY, path);
  else localStorage.removeItem(ACTIVE_SCENE_KEY);
}

export function exportSceneDownload(scene: SceneResource): void {
  const blob = new Blob([serializeScene(scene)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${scene.name.replace(/\s+/g, '_')}.tscn.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function importSceneFile(file: File): Promise<SceneResource> {
  const text = await file.text();
  const scene = parseScene(text);
  scene.builtin = false;
  scene.updatedAt = new Date().toISOString();
  if (!scene.id) scene.id = `user_${Date.now().toString(36)}`;
  return saveScene(scene);
}

export function listScriptsInScene(scene: SceneResource): ScriptRef[] {
  return Object.values(scene.scripts);
}
